import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Package, DollarSign, Target, Diamond, Settings, Gift, User, BarChart3, Edit2, Save, X, Phone, MapPin, Camera, Upload } from 'lucide-react';
import Header from './Header.jsx';

const PageWrapper = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #0b1120 0%, #1a1a2e 100%);
  color: white;
  padding: 1.5rem;
`;

const Container = styled.div`
  max-width: 80rem;
  margin: 0 auto;
`;

const DashboardHeader = styled.div`
  text-align: center;
  margin-top: 2rem;
  margin-bottom: 3rem;
  
  h1 {
    font-size: 2.5rem;
    font-weight: 700;
    margin-bottom: 1rem;
    background: linear-gradient(135deg, #3b82f6, #9333ea);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
  
  .subtitle {
    color: rgba(255, 255, 255, 0.8);
    font-size: 1.1rem;
  }
`;

const DashboardLayout = styled.div`
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: 2rem;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const Sidebar = styled.div`
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(0.5rem);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 1.5rem;
  padding: 2rem;
  height: fit-content;
  
  @media (max-width: 1024px) {
    order: 2;
  }
`;

const ProfileCard = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  
  .avatar {
    width: 80px;
    height: 80px;
    background: linear-gradient(135deg, #3b82f6, #9333ea);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2rem;
    margin: 0 auto 1rem;
  }
  
  .name {
    font-size: 1.3rem;
    font-weight: 600;
    margin-bottom: 0.25rem;
  }
  
  .email {
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.9rem;
    margin-bottom: 1rem;
  }
  
  .membership-badge {
    display: inline-block;
    background: linear-gradient(135deg, #10b981, #059669);
    color: white;
    padding: 0.5rem 1rem;
    border-radius: 9999px;
    font-size: 0.8rem;
    font-weight: 600;
    
    &.premium {
      background: linear-gradient(135deg, #f59e0b, #d97706);
    }
  }
`;

const MenuItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  
  .menu-item {
    padding: 1rem;
    border-radius: 0.75rem;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    
    &:hover {
      background: rgba(255, 255, 255, 0.1);
    }
    
    &.active {
      background: rgba(59, 130, 246, 0.2);
      border-left: 3px solid #3b82f6;
    }
    
    .icon {
      font-size: 1.2rem;
    }
    
    .label {
      font-weight: 500;
    }
  }
`;

const MainContent = styled.div`
  @media (max-width: 1024px) {
    order: 1;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(0.5rem);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 1.5rem;
  padding: 1.5rem;
  
  .stat-icon {
    font-size: 2rem;
    margin-bottom: 1rem;
  }
  
  .stat-value {
    font-size: 2rem;
    font-weight: 700;
    margin-bottom: 0.25rem;
    color: #10b981;
  }
  
  .stat-label {
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.9rem;
  }
`;

const SectionCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(0.5rem);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 1.5rem;
  padding: 2rem;
  margin-bottom: 2rem;
  
  h2 {
    font-size: 1.5rem;
    font-weight: 600;
    margin-bottom: 1.5rem;
    color: white;
  }
`;

const OrdersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const OrderItem = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 1rem;
  padding: 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
  
  .order-info {
    flex: 1;
    
    .order-number {
      font-weight: 600;
      margin-bottom: 0.25rem;
      color: white;
    }
    
    .order-details {
      font-size: 0.9rem;
      color: rgba(255, 255, 255, 0.7);
      margin-bottom: 0.5rem;
    }
    
    .order-date {
      font-size: 0.85rem;
      color: rgba(255, 255, 255, 0.5);
    }
  }
  
  .order-status {
    padding: 0.5rem 1rem;
    border-radius: 9999px;
    font-size: 0.8rem;
    font-weight: 600;
    
    &.delivered {
      background: rgba(16, 185, 129, 0.2);
      color: #10b981;
    }
    
    &.processing {
      background: rgba(59, 130, 246, 0.2);
      color: #3b82f6;
    }
    
    &.pending {
      background: rgba(245, 158, 11, 0.2);
      color: #f59e0b;
    }
  }
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
`;

const BenefitCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 1rem;
  padding: 1.5rem;
  
  .benefit-icon {
    font-size: 2rem;
    margin-bottom: 1rem;
  }
  
  .benefit-title {
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: white;
  }
  
  .benefit-description {
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.7);
  }
`;

const PointsCard = styled.div`
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(147, 51, 234, 0.2));
  border: 2px solid #3b82f6;
  border-radius: 1rem;
  padding: 1.5rem;
  text-align: center;
  
  .points-value {
    font-size: 3rem;
    font-weight: 700;
    color: #3b82f6;
    margin-bottom: 0.5rem;
  }
  
  .points-label {
    color: rgba(255, 255, 255, 0.8);
    margin-bottom: 1rem;
  }
  
  .progress-bar {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 9999px;
    height: 8px;
    margin-bottom: 0.5rem;
    overflow: hidden;
    
    .progress-fill {
      background: linear-gradient(135deg, #3b82f6, #9333ea);
      height: 100%;
      border-radius: 9999px;
      transition: width 0.3s ease;
    }
  }
  
  .next-reward {
    font-size: 0.85rem;
    color: rgba(255, 255, 255, 0.6);
  }
`;

function Dashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [user, setUser] = useState(null);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    photo: ''
  });
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  useEffect(() => {
    // Charger les infos utilisateur
    const userData = localStorage.getItem('inza_user');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      setProfileForm({
        name: parsedUser.name || '',
        email: parsedUser.email || '',
        phone: parsedUser.phone || '',
        address: parsedUser.address || '',
        photo: parsedUser.photo || parsedUser.profile_image || parsedUser.avatar || ''
      });
    } else {
      navigate('/login');
    }
  }, [navigate]);

  const handleEditProfile = () => {
    setIsEditingProfile(true);
  };

  const handleCancelEdit = () => {
    setIsEditingProfile(false);
    if (user) {
      setProfileForm({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        address: user.address || ''
      });
    }
  };

  const handleProfileChange = (field, value) => {
    setProfileForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      // Simuler une sauvegarde API
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const updatedUser = {
        ...user,
        ...profileForm
      };
      
      setUser(updatedUser);
      localStorage.setItem('inza_user', JSON.stringify(updatedUser));
      setIsEditingProfile(false);
      
      console.log('Profil mis à jour:', updatedUser);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du profil:', error);
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        alert('La photo ne doit pas dépasser 5MB');
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        alert('Veuillez sélectionner une image valide');
        return;
      }
      
      setUploadingPhoto(true);
      
      const reader = new FileReader();
      reader.onload = (e) => {
        const photoUrl = e.target.result;
        setProfileForm(prev => ({ ...prev, photo: photoUrl }));
        setUploadingPhoto(false);
      };
      reader.onerror = () => {
        alert('Erreur lors du chargement de la photo');
        setUploadingPhoto(false);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePhoto = () => {
    setProfileForm(prev => ({ ...prev, photo: '' }));
  };

  const getProfilePhoto = () => {
    if (profileForm.photo) {
      return profileForm.photo;
    }
    // Générer une avatar avec les initiales si pas de photo
    const name = profileForm.name || user?.name || 'U';
    const initials = name.split(' ').map(n => n.charAt(0).toUpperCase()).join('').slice(0, 2);
    return null; // Sera géré dans le JSX
  };

  // Données simulées
  const stats = {
    totalOrders: 3,
    totalSpent: 1500000,
    points: 2500,
    savedAmount: 150000
  };

  const orders = [
    {
      id: 12345,
      product: 'iPhone 15 Pro Max',
      config: '256GB • Titane bleu',
      price: 918000,
      status: 'delivered',
      date: '2024-11-15'
    },
    {
      id: 12344,
      product: 'iPhone 15',
      config: '128GB • Noir',
      price: 589000,
      status: 'processing',
      date: '2024-11-28'
    },
    {
      id: 12343,
      product: 'iPhone 15 Pro',
      config: '512GB • Blanc',
      price: 799000,
      status: 'pending',
      date: '2024-12-01'
    }
  ];

  const benefits = [
    {
      icon: '🚚',
      title: 'Livraison Premium',
      description: 'Livraison gratuite et express sur toutes vos commandes'
    },
    {
      icon: '🛡️',
      title: 'Garantie Étendue',
      description: '2 ans de garantie au lieu de 1 an sur tous les produits'
    },
    {
      icon: '💎',
      title: 'Offres VIP',
      description: 'Accès anticipé aux nouvelles collections et ventes privées'
    },
    {
      icon: '🎧',
      title: 'Support Prioritaire',
      description: 'Service client disponible 24/7 avec traitement prioritaire'
    },
    {
      icon: '💰',
      title: 'Cashback 5%',
      description: 'Récupérez 5% de vos achats en points de fidélité'
    },
    {
      icon: '🔄',
      title: 'Reprise Avantageuse',
      description: 'Bonus de +20% sur la reprise de votre ancien iPhone'
    }
  ];

  if (!user) {
    return <div>Chargement...</div>;
  }

  return (
    <PageWrapper>
      <Header />
      <Container>
        <DashboardHeader>
          <h1>Tableau de bord</h1>
          <p className="subtitle">Bienvenue dans votre espace Club INZA</p>
        </DashboardHeader>

        <DashboardLayout>
          <Sidebar>
            <ProfileCard>
              <div 
                className="avatar" 
                style={{
                  background: user?.photo || user?.profile_image || user?.avatar 
                    ? `url(${user?.photo || user?.profile_image || user?.avatar})` 
                    : 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '2rem',
                  fontWeight: 'bold'
                }}
              >
                {!(user?.photo || user?.profile_image || user?.avatar) && 
                  (user?.name ? user.name.charAt(0).toUpperCase() : user?.email?.charAt(0).toUpperCase() || 'U')
                }
              </div>
              <div className="name">{user?.name || 'Utilisateur'}</div>
              <div className="email">{user?.email || ''}</div>
              <div className={`membership-badge ${user?.plan === 'premium' ? 'premium' : ''}`}>
                Club INZA {user?.plan === 'premium' ? 'Premium' : 'Lite'}
              </div>
            </ProfileCard>

            <MenuItems>
              <div 
                className={`menu-item ${activeTab === 'overview' ? 'active' : ''}`}
                onClick={() => setActiveTab('overview')}
              >
                <BarChart3 />
                <span className="label">Aperçu</span>
              </div>
              <div 
                className={`menu-item ${activeTab === 'orders' ? 'active' : ''}`}
                onClick={() => setActiveTab('orders')}
              >
                <Package />
                <span className="label">Mes commandes</span>
              </div>
              <div 
                className={`menu-item ${activeTab === 'benefits' ? 'active' : ''}`}
                onClick={() => setActiveTab('benefits')}
              >
                <Gift />
                <span className="label">Mes avantages</span>
              </div>
              <div 
                className={`menu-item ${activeTab === 'profile' ? 'active' : ''}`}
                onClick={() => setActiveTab('profile')}
              >
                <User />
                <span className="label">Profil</span>
              </div>
            </MenuItems>
          </Sidebar>

          <MainContent>
            {activeTab === 'overview' && (
              <>
                <StatsGrid>
                  <StatCard>
                    <div className="stat-icon"><Package /></div>
                    <div className="stat-value">{stats.totalOrders}</div>
                    <div className="stat-label">Commandes totales</div>
                  </StatCard>
                  <StatCard>
                    <div className="stat-icon"><DollarSign /></div>
                    <div className="stat-value">{(stats.totalSpent / 1000000).toFixed(1)}M</div>
                    <div className="stat-label">FCFA dépensés</div>
                  </StatCard>
                  <StatCard>
                    <div className="stat-icon"><Target /></div>
                    <div className="stat-value">{stats.points.toLocaleString()}</div>
                    <div className="stat-label">Points fidélité</div>
                  </StatCard>
                  <StatCard>
                    <div className="stat-icon"><Diamond /></div>
                    <div className="stat-value">{(stats.savedAmount / 1000).toFixed(0)}k</div>
                    <div className="stat-label">FCFA économisés</div>
                  </StatCard>
                </StatsGrid>

                <SectionCard>
                  <h2><Target /> Points de fidélité</h2>
                  <PointsCard>
                    <div className="points-value">{stats.points.toLocaleString()}</div>
                    <div className="points-label">Points Club INZA</div>
                    <div className="progress-bar">
                      <div className="progress-fill" style={{ width: '65%' }}></div>
                    </div>
                    <div className="next-reward">Plus que 500 points pour le niveau suivant!</div>
                  </PointsCard>
                </SectionCard>

                <SectionCard>
                  <h2><Package /> Commandes récentes</h2>
                  <OrdersList>
                    {orders.slice(0, 2).map(order => (
                      <OrderItem key={order.id}>
                        <div className="order-info">
                          <div className="order-number">Commande #{order.id}</div>
                          <div className="order-details">{order.product} • {order.config}</div>
                          <div className="order-date">{new Date(order.date).toLocaleDateString('fr-FR')}</div>
                        </div>
                        <div className={`order-status ${order.status}`}>
                          {order.status === 'delivered' && 'Livré'}
                          {order.status === 'processing' && 'En traitement'}
                          {order.status === 'pending' && 'En attente'}
                        </div>
                      </OrderItem>
                    ))}
                  </OrdersList>
                </SectionCard>
              </>
            )}

            {activeTab === 'orders' && (
              <SectionCard>
                <h2><Package /> Toutes mes commandes</h2>
                <OrdersList>
                  {orders.map(order => (
                    <OrderItem key={order.id}>
                      <div className="order-info">
                        <div className="order-number">Commande #{order.id}</div>
                        <div className="order-details">{order.product} • {order.config}</div>
                        <div className="order-date">{new Date(order.date).toLocaleDateString('fr-FR')}</div>
                      </div>
                      <div className={`order-status ${order.status}`}>
                        {order.status === 'delivered' && 'Livré'}
                        {order.status === 'processing' && 'En traitement'}
                        {order.status === 'pending' && 'En attente'}
                      </div>
                    </OrderItem>
                  ))}
                </OrdersList>
              </SectionCard>
            )}

            {activeTab === 'benefits' && (
              <SectionCard>
                <h2><Gift /> Mes avantages Club INZA</h2>
                <BenefitsGrid>
                  {benefits.map((benefit, index) => (
                    <BenefitCard key={index}>
                      <div className="benefit-icon"><Gift /></div>
                      <div className="benefit-title">{benefit.title}</div>
                      <div className="benefit-description">{benefit.description}</div>
                    </BenefitCard>
                  ))}
                </BenefitsGrid>
              </SectionCard>
            )}

            {activeTab === 'profile' && (
              <SectionCard>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                  <h2><User /> Mon profil</h2>
                  {!isEditingProfile ? (
                    <button
                      onClick={handleEditProfile}
                      style={{
                        background: 'rgba(59, 130, 246, 0.2)',
                        border: '1px solid rgba(59, 130, 246, 0.5)',
                        borderRadius: '0.5rem',
                        padding: '0.5rem 1rem',
                        color: 'white',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <Edit2 size={16} />
                      Modifier
                    </button>
                  ) : (
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        onClick={handleCancelEdit}
                        style={{
                          background: 'rgba(239, 68, 68, 0.2)',
                          border: '1px solid rgba(239, 68, 68, 0.5)',
                          borderRadius: '0.5rem',
                          padding: '0.5rem 1rem',
                          color: 'white',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          transition: 'all 0.3s ease'
                        }}
                      >
                        <X size={16} />
                        Annuler
                      </button>
                      <button
                        onClick={handleSaveProfile}
                        disabled={saving}
                        style={{
                          background: saving ? 'rgba(156, 163, 175, 0.5)' : 'rgba(34, 197, 94, 0.2)',
                          border: saving ? '1px solid rgba(156, 163, 175, 0.5)' : '1px solid rgba(34, 197, 94, 0.5)',
                          borderRadius: '0.5rem',
                          padding: '0.5rem 1rem',
                          color: 'white',
                          cursor: saving ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          transition: 'all 0.3s ease'
                        }}
                      >
                        <Save size={16} />
                        {saving ? 'Sauvegarde...' : 'Sauvegarder'}
                      </button>
                    </div>
                  )}
                </div>
                
                <div style={{ display: 'grid', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: 'rgba(255, 255, 255, 0.8)' }}>
                      Photo de profil
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                      <div style={{
                        width: '80px',
                        height: '80px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        border: '3px solid rgba(255, 255, 255, 0.2)',
                        position: 'relative'
                      }}>
                        {profileForm.photo ? (
                          <img 
                            src={profileForm.photo} 
                            alt="Photo de profil" 
                            style={{ 
                              width: '100%', 
                              height: '100%', 
                              objectFit: 'cover',
                              borderRadius: '50%'
                            }}
                          />
                        ) : (
                          <span style={{ 
                            color: 'white', 
                            fontSize: '1.5rem', 
                            fontWeight: 'bold' 
                          }}>
                            {(profileForm.name || user?.name || 'U').charAt(0).toUpperCase()}
                          </span>
                        )}
                      </div>
                      
                      {isEditingProfile && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          <input
                            type="file"
                            id="photo-upload"
                            accept="image/*"
                            onChange={handlePhotoUpload}
                            style={{ display: 'none' }}
                          />
                          <label
                            htmlFor="photo-upload"
                            style={{
                              background: 'rgba(59, 130, 246, 0.2)',
                              border: '1px solid rgba(59, 130, 246, 0.5)',
                              borderRadius: '0.5rem',
                              padding: '0.5rem 1rem',
                              color: 'white',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              transition: 'all 0.3s ease',
                              fontSize: '0.875rem'
                            }}
                          >
                            <Upload size={14} />
                            {uploadingPhoto ? 'Chargement...' : 'Changer la photo'}
                          </label>
                          
                          {profileForm.photo && (
                            <button
                              onClick={handleRemovePhoto}
                              style={{
                                background: 'rgba(239, 68, 68, 0.2)',
                                border: '1px solid rgba(239, 68, 68, 0.5)',
                                borderRadius: '0.5rem',
                                padding: '0.5rem 1rem',
                                color: 'white',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                transition: 'all 0.3s ease',
                                fontSize: '0.875rem'
                              }}
                            >
                              <X size={14} />
                              Supprimer
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: 'rgba(255, 255, 255, 0.8)' }}>
                      Nom complet
                    </label>
                    <input 
                      type="text" 
                      value={isEditingProfile ? profileForm.name : (user?.name || '')}
                      onChange={(e) => isEditingProfile && handleProfileChange('name', e.target.value)}
                      style={{ 
                        background: isEditingProfile ? 'rgba(255, 255, 255, 0.15)' : 'rgba(255, 255, 255, 0.1)', 
                        border: isEditingProfile ? '1px solid rgba(59, 130, 246, 0.5)' : '1px solid rgba(255, 255, 255, 0.2)', 
                        borderRadius: '0.5rem', 
                        padding: '0.75rem', 
                        color: 'white', 
                        width: '100%',
                        cursor: isEditingProfile ? 'text' : 'default',
                        opacity: isEditingProfile ? 1 : 0.8
                      }}
                      readOnly={!isEditingProfile}
                    />
                  </div>
                  
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: 'rgba(255, 255, 255, 0.8)' }}>
                      Email
                    </label>
                    <input 
                      type="email" 
                      value={isEditingProfile ? profileForm.email : (user?.email || '')}
                      onChange={(e) => isEditingProfile && handleProfileChange('email', e.target.value)}
                      style={{ 
                        background: isEditingProfile ? 'rgba(255, 255, 255, 0.15)' : 'rgba(255, 255, 255, 0.1)', 
                        border: isEditingProfile ? '1px solid rgba(59, 130, 246, 0.5)' : '1px solid rgba(255, 255, 255, 0.2)', 
                        borderRadius: '0.5rem', 
                        padding: '0.75rem', 
                        color: 'white', 
                        width: '100%',
                        cursor: isEditingProfile ? 'text' : 'default',
                        opacity: isEditingProfile ? 1 : 0.8
                      }}
                      readOnly={!isEditingProfile}
                    />
                  </div>
                  
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: 'rgba(255, 255, 255, 0.8)' }}>
                      <Phone size={16} style={{ marginRight: '0.5rem' }} />
                      Téléphone
                    </label>
                    <input 
                      type="tel" 
                      value={isEditingProfile ? profileForm.phone : (user?.phone || '')}
                      onChange={(e) => isEditingProfile && handleProfileChange('phone', e.target.value)}
                      placeholder="+237 6XX XXX XXX"
                      style={{ 
                        background: isEditingProfile ? 'rgba(255, 255, 255, 0.15)' : 'rgba(255, 255, 255, 0.1)', 
                        border: isEditingProfile ? '1px solid rgba(59, 130, 246, 0.5)' : '1px solid rgba(255, 255, 255, 0.2)', 
                        borderRadius: '0.5rem', 
                        padding: '0.75rem', 
                        color: 'white', 
                        width: '100%',
                        cursor: isEditingProfile ? 'text' : 'default',
                        opacity: isEditingProfile ? 1 : 0.8
                      }}
                      readOnly={!isEditingProfile}
                    />
                  </div>
                  
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: 'rgba(255, 255, 255, 0.8)' }}>
                      <MapPin size={16} style={{ marginRight: '0.5rem' }} />
                      Adresse
                    </label>
                    <textarea
                      value={isEditingProfile ? profileForm.address : (user?.address || '')}
                      onChange={(e) => isEditingProfile && handleProfileChange('address', e.target.value)}
                      placeholder="Votre adresse complète"
                      style={{ 
                        background: isEditingProfile ? 'rgba(255, 255, 255, 0.15)' : 'rgba(255, 255, 255, 0.1)', 
                        border: isEditingProfile ? '1px solid rgba(59, 130, 246, 0.5)' : '1px solid rgba(255, 255, 255, 0.2)', 
                        borderRadius: '0.5rem', 
                        padding: '0.75rem', 
                        color: 'white', 
                        width: '100%',
                        minHeight: '80px',
                        resize: 'vertical',
                        cursor: isEditingProfile ? 'text' : 'default',
                        opacity: isEditingProfile ? 1 : 0.8
                      }}
                      readOnly={!isEditingProfile}
                    />
                  </div>
                  
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: 'rgba(255, 255, 255, 0.8)' }}>
                      Statut Club INZA
                    </label>
                    <div style={{ 
                      background: user?.plan === 'premium' ? 'linear-gradient(135deg, #f59e0b, #d97706)' : 'linear-gradient(135deg, #10b981, #059669)',
                      color: 'white', 
                      padding: '0.75rem', 
                      borderRadius: '0.5rem', 
                      textAlign: 'center',
                      fontWeight: '600',
                      opacity: 0.9
                    }}>
                      Club INZA {user?.plan === 'premium' ? 'Premium' : 'Lite'}
                    </div>
                  </div>
                </div>
              </SectionCard>
            )}
          </MainContent>
        </DashboardLayout>
      </Container>
    </PageWrapper>
  );
}

export default Dashboard;
